import { NavLink } from 'react-router-dom';
import { ChevronsLeft, ChevronsRight, UtensilsCrossed } from 'lucide-react';
import { cn } from '@/lib/cn';
import { formatCombo } from '@/hooks/useHotkeys';
import type { VisibleNavItem } from '@/routes/useNavItems';

export function Sidebar({
  items,
  collapsed: collapsedProp,
  onToggle,
  drawer = false,
  onNavigate,
}: {
  items: VisibleNavItem[];
  collapsed: boolean;
  onToggle: () => void;
  /** Rendered inside the mobile drawer: always full width, the toggle button closes the drawer. */
  drawer?: boolean;
  onNavigate?: () => void;
}) {
  const collapsed = drawer ? false : collapsedProp;
  return (
    <aside
      aria-label="Sidebar"
      className={cn(
        'flex shrink-0 flex-col bg-sidebar text-sidebar-fg',
        drawer ? 'h-full w-72 max-w-[85vw]' : 'transition-[width] duration-150',
        !drawer && (collapsed ? 'w-16' : 'w-64'),
      )}
    >
      <div className="flex h-14 items-center gap-3 px-4 text-white">
        <UtensilsCrossed aria-hidden className="size-6 shrink-0 text-primary" />
        {!collapsed && <span className="truncate font-bold">Restaurant POS</span>}
      </div>

      {/* `relative`: the rail's sr-only labels are absolutely positioned; without a positioned ancestor they escape this
          scroll container and add page scroll when the items overflow a short viewport. */}
      <nav aria-label="Main" className="relative min-h-0 flex-1 overflow-y-auto px-2 py-2">
        <ul className="flex flex-col gap-0.5">
          {items.map((item, i) => {
            const Icon = item.icon;
            const prev = items[i - 1];
            const divider = prev && prev.group !== item.group;
            return (
              <li key={item.id} className={cn(divider && 'mt-2 border-t border-white/10 pt-2')}>
                <NavLink
                  to={item.path}
                  onClick={onNavigate}
                  title={
                    collapsed
                      ? `${item.label}${item.shortcut ? ` (${formatCombo(item.shortcut)})` : ''}`
                      : undefined
                  }
                  className={({ isActive }) =>
                    cn(
                      'group flex h-11 items-center gap-3 rounded-lg px-3 text-sm font-medium transition-colors',
                      'focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-ring',
                      isActive
                        ? 'bg-sidebar-active text-white'
                        : 'hover:bg-white/10 hover:text-white',
                    )
                  }
                >
                  <Icon aria-hidden className="size-5 shrink-0" />
                  {!collapsed && <span className="min-w-0 flex-1 truncate">{item.label}</span>}
                  {!collapsed && item.shortcut && (
                    <span
                      aria-hidden
                      className="text-[10px] tracking-wide opacity-60 group-aria-[current=page]:opacity-100"
                    >
                      {formatCombo(item.shortcut)}
                    </span>
                  )}
                  {collapsed && <span className="sr-only">{item.label}</span>}
                </NavLink>
              </li>
            );
          })}
        </ul>
      </nav>

      <button
        type="button"
        onClick={onToggle}
        aria-label={drawer ? 'Close menu' : collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        aria-expanded={drawer ? undefined : !collapsed}
        title={drawer ? 'Close menu (Esc)' : 'Toggle sidebar (Alt+B)'}
        className="flex h-12 items-center gap-3 border-t border-white/10 px-5 text-sm hover:bg-white/10 hover:text-white focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-ring"
      >
        {collapsed ? <ChevronsRight className="size-5" /> : <ChevronsLeft className="size-5" />}
        {!collapsed && <span>{drawer ? 'Close' : 'Collapse'}</span>}
      </button>
    </aside>
  );
}
